import { useMemo, useState } from 'react';
import { EmbedWidget } from './components/EmbedWidget';

export const DemoPage: React.FC = () => {
  const [exhibitorSlug, setExhibitorSlug] = useState('dhowa-technos');
  const [librarySlug, setLibrarySlug] = useState('moxa');
  const [mode, setMode] = useState<'modal' | 'link'>('modal');
  const [href, setHref] = useState('https://evort.com/');

  const embedCode = useMemo(() => {
    const attrs = [
      'data-thumbnail-widget',
      `data-exhibitor-slug="${exhibitorSlug}"`,
      `data-library-slug="${librarySlug}"`
    ];

    if (mode === 'link') {
      attrs.push('data-mode="link"');
      if (href) {
        attrs.push(`data-href="${href}"`);
      }
    }

    const attributeString = attrs.join('\n  ');

    return `<script src="https://capture-viewer.evort.com/embed.js"></script>\n<div\n  ${attributeString}\n></div>`;
  }, [exhibitorSlug, librarySlug, mode, href]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <header className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Thumbnail Widget Demo
          </h1>
          <p className="text-xl text-gray-600">
            サムネイルカードを使ったモーダル／リンク遷移ウィジェットのデモページ
          </p>
        </header>

        <div className="max-w-2xl mx-auto">
          {/* 設定パネル */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-8">
            <h2 className="text-2xl font-semibold mb-6">設定</h2>
            
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exhibitor Slug
                  </label>
                  <input
                    type="text"
                    value={exhibitorSlug}
                    onChange={(e) => setExhibitorSlug(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: dhowa-technos"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Library Slug
                  </label>
                  <input
                    type="text"
                    value={librarySlug}
                    onChange={(e) => setLibrarySlug(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="例: product-library"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    モード
                  </label>
                  <select
                    value={mode}
                    onChange={(e) => setMode(e.target.value as 'modal' | 'link')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="modal">モーダル表示</option>
                    <option value="link">リンク遷移</option>
                  </select>
                </div>
                {mode === 'link' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      遷移先リンク (href)
                    </label>
                    <input
                      type="url"
                      value={href}
                      onChange={(e) => setHref(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="例: https://evort.com/"
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-medium mb-4">埋め込みコード</h3>
              <div className="bg-gray-100 p-4 rounded-md text-sm font-mono overflow-x-auto">
                <pre>{embedCode}</pre>
              </div>
            </div>
          </div>

          {/* プレビュー */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-2xl font-semibold mb-6">プレビュー</h2>
            
            <div className="flex items-center justify-center min-h-[650px]">
              <EmbedWidget
                exhibitorSlug={exhibitorSlug}
                librarySlug={librarySlug}
                mode={mode}
                href={mode === 'link' ? href : undefined}
              />
            </div>

            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <h3 className="text-sm font-semibold text-blue-900 mb-2">動作確認</h3>
              <p className="text-sm text-blue-700">
                サムネイルカードをクリックすると、
                {mode === 'link'
                  ? '指定したリンク先を新しいタブで開きます。'
                  : 'モーダルウィンドウが開きます。'}
                {mode === 'modal'
                  ? 'モーダル内にはiframeで指定したURLが表示されます。'
                  : 'href が未設定の場合はモーダル表示にフォールバックします。'}
              </p>
            </div>
          </div>
        </div>

        <footer className="text-center mt-12 py-8 border-t border-gray-200">
          <p className="text-gray-600">
            Thumbnail Widget Demo - サムネイル → モーダル／リンク遷移ウィジェット
          </p>
        </footer>
      </div>
    </div>
  );
};
